// @ts-strict-ignore
import { MIXPANEL_TOKEN } from "config";
import { EVENTS } from "./events";
import type { QueuedEvent } from "../";
import { captureException } from "observability";

let mixpanelLoaded = false;
const queuedEvents: QueuedEvent[] = [];

function formatPageName(url: string) {
  if (url === "/") {
    return "Homepage";
  }
  return removeTrailingSlashes(url); //Removes one or more trailing slashes from URL
}

function removeTrailingSlashes(url: string) {
  return url.replace(/\/+$/, ""); //Removes one or more trailing slashes from URL
}

// dynamically loaded
let mixpanel = null;

export const mixpanelClient = {
  initializeMixpanel: async (optIn = true) => {
    // Dynamically load mixpanel
    mixpanel = (await import("mixpanel-browser")).default;

    const config = {
      secure_cookie: true,
      api_host:
        process.env.NEXT_PUBLIC_MIXPANEL_HOST || "https://api.mixpanel.com",
      ip: false,
      opt_out_tracking_by_default: !optIn,
      loaded: () => {
        mixpanelLoaded = true;
        while (queuedEvents.length) {
          const { method, args } = queuedEvents.shift();
          mixpanel[method](...args);
        }
      }
    };
    mixpanel.init(MIXPANEL_TOKEN, config);
  },

  trackUTMs: function () {
    const eventNames = {
      utm_source: "UTM Source [Last Touch]",
      utm_medium: "UTM Medium [Last Touch]",
      utm_campaign: "UTM Campaign [Last Touch]",
      utm_term: "UTM Term [Last Touch]",
      utm_content: "UTM Content [Last Touch]",
      utm_adgroup: "UTM Ad Group [Last Touch]",
      utm_adgroupID: "UTM Ad Group ID [Last Touch]",
      utm_campaignID: "UTM Campaign ID [Last Touch]",
      utm_country: "UTM Country [Last Touch]",
      utm_creative: "UTM Creative [Last Touch]",
      utm_creativeID: "UTM Creative ID [Last Touch]"
    };
    const campaign_keywords = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
      "utm_adgroup",
      "utm_adgroupID",
      "utm_campaignID",
      "utm_country",
      "utm_creative",
      "utm_creativeID"
    ];
    let kw = "";
    const params = {};
    let index;
    for (index = 0; index < campaign_keywords.length; index += 1) {
      kw = getQueryParam(document.URL, campaign_keywords[index]);
      if (kw.length) {
        params[eventNames[campaign_keywords[index]]] = kw;
      }
    }
    this.setSuperProperty(params);
  },

  track: (eventName: string, properties?: any) => {
    if (mixpanelLoaded) {
      mixpanel.track(eventName, properties);
    } else {
      queuedEvents.push({
        method: "track",
        args: [eventName, properties]
      });
    }
  },

  trackPageView: function () {
    // Track UTMs:
    this.trackUTMs();
    this.setSuperPropertyOnce({ "New Public Site": true });
    const eventName = EVENTS.PAGE_VIEW.name;
    const properties = {
      [EVENTS.PAGE_VIEW.properties.PAGE_NAME]:
        `Public: ${formatPageName(location.pathname)}`
    };
    if (mixpanelLoaded) {
      mixpanel.track(eventName, properties);
    } else {
      queuedEvents.push({
        method: "track",
        args: [eventName, properties]
      });
    }
  },

  trackLinkClick: (
    trackingID: string,
    isRegisterLink: boolean,
    extraProperties?: Record<string, any>
  ) => {
    if (!trackingID) {
      return;
    }
    const trackingId = `#${trackingID}`;
    const eventName = isRegisterLink
      ? EVENTS.START_REGISTRATION.name
      : "Clicked Link";
    const properties = {
      trigger: `Public: ${trackingID}`,
      ...(extraProperties || {})
    };

    // Check if this is an experiment-related link
    const isExperimentLink =
      trackingID.includes("treatment-") || trackingID.includes("control-");

    if (isExperimentLink && !isRegisterLink) {
      // Extract experiment info from the ID
      const parts = trackingID.split("-");
      if (parts.length >= 3) {
        const elementType = parts[0] + "-" + parts[1];
        const variation = parts[2];
        const experimentName = parts.slice(3).join("-");

        // Send experiment-specific click event
        mixpanelClient.track(EVENTS.EXPERIMENT_LINK_CLICKED.name, {
          [EVENTS.EXPERIMENT_LINK_CLICKED.properties.EXPERIMENT_NAME]:
            experimentName,
          [EVENTS.EXPERIMENT_LINK_CLICKED.properties.EXPERIMENT_VARIATION]:
            variation,
          [EVENTS.EXPERIMENT_LINK_CLICKED.properties.LINK_ID]: trackingID,
          [EVENTS.EXPERIMENT_LINK_CLICKED.properties.ELEMENT_TYPE]: elementType
        });
      }
    }

    if (mixpanelLoaded) {
      mixpanel.track_links(trackingId, eventName, properties);
    } else {
      queuedEvents.push({
        method: "track_links",
        args: [trackingId, eventName, properties]
      });
    }
  },

  setSuperProperty: (superProperty: object) => {
    if (mixpanelLoaded) {
      mixpanel.register(superProperty);
    } else {
      queuedEvents.push({
        method: "register",
        args: [superProperty]
      });
    }
  },
  setSuperPropertyOnce: (superProperty: object) => {
    if (mixpanelLoaded) {
      mixpanel.register_once(superProperty);
    } else {
      queuedEvents.push({
        method: "register_once",
        args: [superProperty]
      });
    }
  },
  aliasId: (newId: string) => {
    if (mixpanelLoaded) {
      mixpanel.alias(newId);
    } else {
      queuedEvents.push({
        method: "alias",
        args: [newId]
      });
    }
  },
  optInTracking: () => mixpanelLoaded && mixpanel.optInTracking()
};

function getQueryParam(url: string, param: string) {
  try {
    // Expects a raw URL
    param = param.replace(/[[]/, "[").replace(/[]]/, "]");
    const regexS = "[?&]" + param + "=([^&#]*)",
      regex = new RegExp(regexS),
      results: any = regex.exec(url);
    if (
      results === null ||
      (results && typeof results[1] !== "string" && results[1].length)
    ) {
      return "";
    } else {
      return decodeURIComponent(results[1]).replace(/\W/gi, " ");
    }
  } catch (e) {
    captureException(e as Error);
  }
}
